/**
 * Repair and rearm view-model (spec §7.3): hull points missing and their cost (all or a partial amount), and the
 * torpedoes missing and their cost. Full hull and racks are the start ship's (the only player ship in M2).
 */
import { EPSILON } from '../../career/ops'
import { goodT } from '../../career/parts'
import { holdTorpedoT, platingBillOf, repairCost, repairPlatingT, SERVICE_TEXTS, TORPEDO_T } from '../../career/services'
import type { Career } from '../../career/types'
import type { M2Data } from '../../data/m2Schemas'
import type { MarketQuote } from '../../strategic-link/market'
import { formatCr, formatTonnes } from '../format'

export interface RepairModel {
  hull: number
  hullMax: number
  hullLabel: string
  /** Whole hull points missing (rounded up). */
  missing: number
  repairAllCost: number
  repairAllLabel: string
  canRepair: boolean
  torpedoes: number
  torpedoesMax: number
  torpedoLabel: string
  missingTorpedoes: number
  rearmAllCost: number
  rearmAllLabel: string
  canRearm: boolean
  repairPerHull: number
  torpedoPrice: number
  /** M8 SNK-3b: the flown def's strategic plating bill (t), or null (credit repairs only). */
  platingBill: number | null
  /** M8 SNK-3b: hull plating in the hold (t), used first by a plating repair. */
  holdPlating: number
  /** M8 SNK-5: weapon parts in the hold (t), used first by a linked rearm. */
  holdWeaponParts: number
  /**
   * K12: the strategic economy prices the docked station (the runtime's `strategicAt`), so repairs and rearms there
   * are paid in goods even where none is quoted (a refinery or habitat: hold only); false: credits; null: unknown (no
   * world provider: goods show only where quoted).
   */
  linked: boolean | null
}

export function repairModel(data: M2Data, career: Career, linked: boolean | null = null): RepairModel {
  const hullMax = data.career.start.hull
  const tMax = data.career.start.torpedoes
  const exact = Math.max(0, hullMax - career.ship.hull)
  const missing = exact > EPSILON ? Math.ceil(exact - EPSILON) : 0
  const allCost = exact > EPSILON ? repairCost(data, exact) : 0
  const missingT = Math.max(0, tMax - career.ship.torpedoes)
  const rearmCost = missingT * data.career.torpedoPrice
  return {
    hull: career.ship.hull,
    hullMax,
    hullLabel: `${Math.floor(career.ship.hull + EPSILON)} / ${hullMax}`,
    missing,
    repairAllCost: allCost,
    repairAllLabel: missing > 0 ? `Repair all ${missing} hull: ${formatCr(allCost)}` : 'Hull intact',
    canRepair: missing > 0,
    torpedoes: career.ship.torpedoes,
    torpedoesMax: tMax,
    torpedoLabel: `${career.ship.torpedoes} / ${tMax}`,
    missingTorpedoes: missingT,
    rearmAllCost: rearmCost,
    rearmAllLabel: missingT > 0 ? `Rearm ${missingT} torpedo${missingT === 1 ? '' : 'es'}: ${formatCr(rearmCost)}` : 'Racks full',
    canRearm: missingT > 0,
    repairPerHull: data.career.repairPerHull,
    torpedoPrice: data.career.torpedoPrice,
    platingBill: platingBillOf(career.ship.defId),
    holdPlating: goodT(career.cargo.parts, 'hull_plating'),
    holdWeaponParts: goodT(career.cargo.parts, 'weapon_parts'),
    linked,
  }
}

/** Cost label of a partial repair of `points` hull. */
export function partialRepairLabel(data: M2Data, points: number): string {
  return points > 0 ? `Repair ${points} hull: ${formatCr(repairCost(data, points))}` : 'Repair'
}

/** Cost label of rearming `count` torpedoes. */
export function partialRearmLabel(data: M2Data, count: number): string {
  return count > 0 ? `Rearm ${count}: ${formatCr(count * data.career.torpedoPrice)}` : 'Rearm'
}

/** The docked station's plating buy quote for `t` t (`UiActions.quoteGood`), or null where none is quoted. */
export type PlatingQuote = (t: number) => MarketQuote | null

/** A service paid in goods as the screen shows it: the button label and the station's stock note (or null). */
export interface GoodsServiceLabel {
  label: string
  /** "No hull plating stocked here." where the station quotes none and the hold pays what it can; else null. */
  note: string | null
}

/**
 * The label of `needT` t of a good with `fromHold` t from the hold and the rest quoted by `quote` (`head`: "Repair
 * all 120 hull", `good`: "plating"), or null for the credit label. `linked` false is always credits. Linked, a
 * station that quotes none (refinery, habitat) shows the goods label with the hold part and `none` as its note (the
 * runtime repairs from the hold only there). Unknown (`null`), goods show only where quoted or the hold covers all.
 */
function goodsLabel(linked: boolean | null, needT: number, fromHold: number, quote: PlatingQuote | undefined, head: string, good: string, none: string): GoodsServiceLabel | null {
  if (linked === false || (linked === null && quote === undefined)) return null
  const rest = needT - fromHold
  const q = rest > EPSILON && quote !== undefined ? quote(rest) : null
  if (rest > EPSILON && q === null && linked !== true) return null
  const hold = fromHold > EPSILON ? ` (${formatTonnes(fromHold)} t from hold)` : ''
  const cost = q === null ? '' : `, ${formatCr(q.total)}`
  return { label: `${head}: ${formatTonnes(needT)} t ${good}${hold}${cost}`, note: rest > EPSILON && q === null ? none : null }
}

/**
 * M8 SNK-3b: a repair of `points` hull paid in hull plating, e.g. "Repair all 120 hull: 3.1 t plating (1.0 t from
 * hold), 2,625 cr", or null for the credit label. Plating shows where the strategic economy prices the docked station
 * (`model.linked`; at a refinery or habitat with the hold part only and "No hull plating stocked here."), or, when
 * that is unknown, where the station quotes plating or the hold alone covers the repair; `prefix` is "Repair" or
 * "Repair all".
 */
export function platingRepair(model: RepairModel, points: number, quote: PlatingQuote | undefined, prefix = 'Repair'): GoodsServiceLabel | null {
  const bill = model.platingBill
  if (bill === null || !(points > 0)) return null
  const needT = repairPlatingT(points, model.hullMax, bill)
  const held = model.holdPlating
  return goodsLabel(model.linked, needT, held < needT ? held : needT, quote, `${prefix} ${points} hull`, 'plating', SERVICE_TEXTS.noPlating)
}

/** `platingRepair`'s label (null: the credit label). */
export function platingRepairLabel(model: RepairModel, points: number, quote: PlatingQuote | undefined, prefix = 'Repair'): string | null {
  return platingRepair(model, points, quote, prefix)?.label ?? null
}

/**
 * M8 SNK-5: a rearm of `count` torpedoes paid in weapon parts (`torpedoT` t each, hold first in whole torpedoes),
 * e.g. "Rearm 4: 2.0 t weapon parts (0.5 t from hold), 2,475 cr", or null for the credit label; the same rule as
 * `platingRepair` with "No weapon parts stocked here.". `quote` is the station's weapon-parts buy quote.
 */
export function weaponPartsRearm(model: RepairModel, count: number, quote: PlatingQuote | undefined, prefix = 'Rearm'): GoodsServiceLabel | null {
  if (!(count > 0)) return null
  const needT = count * TORPEDO_T
  return goodsLabel(model.linked, needT, holdTorpedoT(model.holdWeaponParts, count), quote, `${prefix} ${count}`, 'weapon parts', SERVICE_TEXTS.noWeaponParts)
}

/** `weaponPartsRearm`'s label (null: the credit label). */
export function weaponPartsRearmLabel(model: RepairModel, count: number, quote: PlatingQuote | undefined, prefix = 'Rearm'): string | null {
  return weaponPartsRearm(model, count, quote, prefix)?.label ?? null
}

/**
 * R3-3: the Hull figure's rate note: the goods form wherever a repair here is paid in plating (the station's stock note,
 * else "paid in hull plating"), also with the hull intact (the bill of a full repair decides), else the credit rate.
 */
export function repairRateNote(model: RepairModel, quote: PlatingQuote | undefined): string {
  const row = platingRepair(model, model.missing > 0 ? model.missing : model.hullMax, quote)
  return row === null ? `${formatCr(model.repairPerHull)} per point` : (row.note ?? 'paid in hull plating')
}

/** R3-3: the Racks figure's rate note: `repairRateNote`'s rule for weapon parts (racks full: a full rearm's bill). */
export function rearmRateNote(model: RepairModel, quote: PlatingQuote | undefined): string {
  const row = weaponPartsRearm(model, model.missingTorpedoes > 0 ? model.missingTorpedoes : model.torpedoesMax, quote)
  return row === null ? `${formatCr(model.torpedoPrice)} each` : (row.note ?? 'paid in weapon parts')
}
